import classNames from 'classnames';

import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import Tooth10Normal from '../../../../svgs/odontogram-svgs/components/Tooth10Normal';
import Tooth11Normal from '../../../../svgs/odontogram-svgs/components/Tooth11Normal';
import Tooth12Normal from '../../../../svgs/odontogram-svgs/components/Tooth12Normal';
import Tooth13Normal from '../../../../svgs/odontogram-svgs/components/Tooth13Normal';
import Tooth14Normal from '../../../../svgs/odontogram-svgs/components/Tooth14Normal';
import Tooth15Normal from '../../../../svgs/odontogram-svgs/components/Tooth15Normal';
import Tooth16Normal from '../../../../svgs/odontogram-svgs/components/Tooth16Normal';
import Tooth17Normal from '../../../../svgs/odontogram-svgs/components/Tooth17Normal';
import Tooth18Normal from '../../../../svgs/odontogram-svgs/components/Tooth18Normal';
import Tooth19Normal from '../../../../svgs/odontogram-svgs/components/Tooth19Normal';
import Tooth1Normal from '../../../../svgs/odontogram-svgs/components/Tooth1Normal';
import Tooth20Normal from '../../../../svgs/odontogram-svgs/components/Tooth20Normal';
import Tooth21Normal from '../../../../svgs/odontogram-svgs/components/Tooth21Normal';
import Tooth22Normal from '../../../../svgs/odontogram-svgs/components/Tooth22Normal';
import Tooth23Normal from '../../../../svgs/odontogram-svgs/components/Tooth23Normal';
import Tooth24Normal from '../../../../svgs/odontogram-svgs/components/Tooth24Normal';
import Tooth25Normal from '../../../../svgs/odontogram-svgs/components/Tooth25Normal';
import Tooth26Normal from '../../../../svgs/odontogram-svgs/components/Tooth26Normal';
import Tooth27Normal from '../../../../svgs/odontogram-svgs/components/Tooth27Normal';
import Tooth28Normal from '../../../../svgs/odontogram-svgs/components/Tooth28Normal';
import Tooth29Normal from '../../../../svgs/odontogram-svgs/components/Tooth29Normal';
import Tooth2Normal from '../../../../svgs/odontogram-svgs/components/Tooth2Normal';
import Tooth30Normal from '../../../../svgs/odontogram-svgs/components/Tooth30Normal';
import Tooth31Normal from '../../../../svgs/odontogram-svgs/components/Tooth31Normal';
import Tooth32Normal from '../../../../svgs/odontogram-svgs/components/Tooth32Normal';
import Tooth3Normal from '../../../../svgs/odontogram-svgs/components/Tooth3Normal';
import Tooth4Normal from '../../../../svgs/odontogram-svgs/components/Tooth4Normal';
import Tooth5Normal from '../../../../svgs/odontogram-svgs/components/Tooth5Normal';
import Tooth6Normal from '../../../../svgs/odontogram-svgs/components/Tooth6Normal';
import Tooth7Normal from '../../../../svgs/odontogram-svgs/components/Tooth7Normal';
import Tooth8Normal from '../../../../svgs/odontogram-svgs/components/Tooth8Normal';
import Tooth9Normal from '../../../../svgs/odontogram-svgs/components/Tooth9Normal';

const teethOrder = {
  UR: { start: 0, end: 8 },
  UL: { start: 8, end: 16 },
  LR: { start: 24, end: 32 },
  LL: { start: 16, end: 24 },
};

// Only permanent teeth (1-32) for this simplified odontogram
const toothComponents: Record<string, React.ComponentType<any>> = {
  '1': Tooth1Normal,
  '2': Tooth2Normal,
  '3': Tooth3Normal,
  '4': Tooth4Normal,
  '5': Tooth5Normal,
  '6': Tooth6Normal,
  '7': Tooth7Normal,
  '8': Tooth8Normal,
  '9': Tooth9Normal,
  '10': Tooth10Normal,
  '11': Tooth11Normal,
  '12': Tooth12Normal,
  '13': Tooth13Normal,
  '14': Tooth14Normal,
  '15': Tooth15Normal,
  '16': Tooth16Normal,
  '17': Tooth17Normal,
  '18': Tooth18Normal,
  '19': Tooth19Normal,
  '20': Tooth20Normal,
  '21': Tooth21Normal,
  '22': Tooth22Normal,
  '23': Tooth23Normal,
  '24': Tooth24Normal,
  '25': Tooth25Normal,
  '26': Tooth26Normal,
  '27': Tooth27Normal,
  '28': Tooth28Normal,
  '29': Tooth29Normal,
  '30': Tooth30Normal,
  '31': Tooth31Normal,
  '32': Tooth32Normal,
};

const quadrants = ['UR', 'UL', 'LR', 'LL'] as const;

interface CompareOdontogramProps {
  onToothSelect?: (toothId: ValidUniversalToothId) => void;
  selectedTeeth?: ValidUniversalToothId[];
}

export const CompareOdontogram = ({
  onToothSelect,
  selectedTeeth = [],
}: CompareOdontogramProps) => {
  const getQuadrantTeeth = (
    quadrant: (typeof quadrants)[number],
  ): ValidUniversalToothId[] => {
    const teethStart = teethOrder[quadrant].start;
    const teethEnd = teethOrder[quadrant].end;
    const teeth: ValidUniversalToothId[] = [];

    for (let i = teethStart; i < teethEnd; i++) {
      teeth.push((i + 1).toString() as ValidUniversalToothId);
    }

    // Reverse order for lower quadrants
    if (quadrant === 'LR' || quadrant === 'LL') {
      teeth.reverse();
    }

    return teeth;
  };

  const handleToothClick = (toothId: ValidUniversalToothId) => {
    onToothSelect?.(toothId);
  };

  const isLowerQuadrant = (quadrant: (typeof quadrants)[number]) => {
    return quadrant === 'LR' || quadrant === 'LL';
  };

  return (
    <div
      className="vai-flex vai-justify-center vai-items-center vai-p-2 vai-rounded-md"
      data-testid="compare-odontogram"
    >
      <div className="vai-relative vai-grid vai-grid-cols-2 vai-grid-rows-2 vai-gap-[4px_12px] vai-text-xs">
        {quadrants.map((quadrant) => {
          const quadrantTeeth = getQuadrantTeeth(quadrant);

          return (
            <div
              key={quadrant}
              className={classNames(
                'vai-py-1 vai-rounded-[2px] vai-flex vai-relative vai-gap-[6px]',
                {
                  'vai-align-bottom vai-justify-center':
                    !isLowerQuadrant(quadrant),
                  'vai-align-top vai-justify-center': isLowerQuadrant(quadrant),
                },
              )}
            >
              {quadrantTeeth.map((toothId) => {
                const ToothComponent = toothComponents[toothId];
                const isSelected = selectedTeeth.includes(toothId);

                return (
                  <button
                    key={toothId}
                    className={classNames(
                      'vai-flex vai-justify-center vai-min-w-0 vai-rounded-[2px] vai-transition-all vai-duration-200',
                      {
                        'vai-items-end': !isLowerQuadrant(quadrant), // Bottom aligned for upper quadrants
                        'vai-items-start': isLowerQuadrant(quadrant), // Top aligned for lower quadrants
                        'vai-shadow-[0_0_0_2px_#778497] vai-bg-[#D9D9D933]':
                          isSelected,
                        'hover:vai-shadow-[0_0_0_2px_#778497] hover:vai-bg-[#D9D9D933]':
                          !isSelected,
                      },
                    )}
                    onClick={() => handleToothClick(toothId)}
                    title={`Tooth ${toothId}`}
                  >
                    <ToothComponent
                      className="vai-w-full vai-h-auto vai-max-h-full"
                      fill="#94A3B8"
                      stroke="#A1A1AA"
                      findingsFill=""
                      wx={0.7}
                      hx={0.7}
                    />
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
